import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  AppButton,
  Box,
  FlatTextInput,
  Form,
  getDuration,
  getValidationResolver,
  Preloader,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { AUTH_ERROR_KEY } from '@/common/constants';
import useNavigateToPasswordResetLinkSent from '@/modules/user/components/SignIn/hooks/useNavigateToPasswordResetLinkSent';
import {
  FORGOT_USERNAME_ATTEMPTS_LIMIT_ERROR_CODE,
  INVALID_USER_NAME_ERROR_CODE,
} from '@/modules/user/components/SignIn/signIn.constants';

import { useResendCodeTimerStore } from '../stores/useResendCodeTimer.store';

import { useForgotPassword } from './hooks';

const USERNAME_FIELD_KEY = 'forgotUsername';
interface ForgotPasswordProps {
  username: string;
}

export const REQUIRED_TEXT = 'This field is required';
const ForgotPassword: React.FC<ForgotPasswordProps> = ({ username }) => {
  const { navigateToPasswordResetLinkSent } = useNavigateToPasswordResetLinkSent();
  const { setTimer } = useResendCodeTimerStore();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      [USERNAME_FIELD_KEY]: username,
    },

    resolver: getValidationResolver({
      [USERNAME_FIELD_KEY]: yup.string().required(REQUIRED_TEXT),
    }),
  });

  const { handleRequestCode, isLoading } = useForgotPassword();
  const handleSubmitForgotPassword = useCallback(
    async (value: object) => {
      const currentUsername = _.get(value, USERNAME_FIELD_KEY, '') as string;

      try {
        await handleRequestCode(currentUsername);
        setTimer({ expiredIn: `${Date.now() + getDuration({ minutes: 1 })}` });
        navigateToPasswordResetLinkSent(currentUsername);
      } catch (error) {
        const elevateErrorCode = _.get(error, 'response.data.elevate_error_code', '') as string;

        if (elevateErrorCode === INVALID_USER_NAME_ERROR_CODE) {
          setError(USERNAME_FIELD_KEY, { message: AUTH_ERROR_KEY.USER_FORMAT_INVALID });
          return;
        }

        if (elevateErrorCode === FORGOT_USERNAME_ATTEMPTS_LIMIT_ERROR_CODE) {
          setError(USERNAME_FIELD_KEY, { message: AUTH_ERROR_KEY.TOO_MANY_FAILED_ATTEMPTS });
        }
      }
    },
    [
      handleRequestCode,
      setError,
      setTimer,
      navigateToPasswordResetLinkSent,
    ]
  );

  return (
    <Form autoComplete="off" onSubmit={handleSubmit(handleSubmitForgotPassword)}>
      <Box dir="column">
        <Controller
          name={USERNAME_FIELD_KEY}
          control={control}
          render={({ field: { value, onChange } }) => (
            <FlatTextInput
              value={value}
              onChange={onChange}
              className="text-input"
              testId="text-input-username"
              defaultAutoComplete="off"
              name={USERNAME_FIELD_KEY}
              placeholder="Enter your username"
              onBlur={() => {
                onChange(value?.trim());
              }}
              errorText={_.get(errors, `${USERNAME_FIELD_KEY}.message`)}
              style={{
                minHeight: '54px',
              }}
              isLowerCaseMode
            />
          )}
        />
        <Box margin={{ top: 'spacing32' }}>
          <AppButton
            testId="forgot-password-continue"
            type="submit"
            width="100%"
            size="L"
            disabled={isLoading}
          >
            {isLoading ? <Preloader color="white" /> : 'Continue'}
          </AppButton>
        </Box>
      </Box>
    </Form>
  );
};

export default ForgotPassword;
